import { useState } from 'react';
import { Plus, Edit, Trash2, X, Save, Tag } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import { useSettings } from '../../context/SettingsContext';
import toast from 'react-hot-toast';

const DEFAULT_CATEGORIES = [
  { id: 1, name: 'Laptops', icon: '💻', description: 'Portable computers for professionals', productCount: 12, color: '#7c6dfa' },
  { id: 2, name: 'Smartphones', icon: '📱', description: 'Latest mobile devices', productCount: 8, color: '#8b5cf6' },
  { id: 3, name: 'Tablets', icon: '📟', description: 'Tablets and e-readers', productCount: 5, color: '#10b981' },
  { id: 4, name: 'Audio', icon: '🎧', description: 'Headphones, speakers and more', productCount: 9, color: '#06b6d4' },
  { id: 5, name: 'Cameras', icon: '📷', description: 'Digital cameras and accessories', productCount: 6, color: '#f59e0b' },
  { id: 6, name: 'Accessories', icon: '🖱️', description: 'Keyboards, mice, cables', productCount: 15, color: '#ef4444' },
  { id: 7, name: 'Gaming', icon: '🎮', description: 'Gaming gear and consoles', productCount: 4, color: '#ec4899' },
  { id: 8, name: 'Networking', icon: '📡', description: 'Routers, switches, modems', productCount: 3, color: '#84cc16' },
];

const EMPTY = { name: '', icon: '📦', description: '', color: '#7c6dfa' };

export default function AdminCategories() {
  const { t } = useSettings();
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = (e) => {
    e.preventDefault();
    if (modal === 'new') {
      setCategories(prev => [...prev, { ...form, id: Date.now(), productCount: 0 }]);
      toast.success('Category created!');
    } else {
      setCategories(prev => prev.map(c => c.id === modal.id ? { ...c, ...form } : c));
      toast.success('Category updated!');
    }
    setModal(null);
  };

  const handleDelete = (id, name) => {
    if (!window.confirm(`Delete category "${name}"?`)) return;
    setCategories(prev => prev.filter(c => c.id !== id));
    toast.success('Category deleted');
  };

  return (
    <AdminLayout title={t('admin', 'categories')}>
      <div className="admin-page-header flex-between" style={{ marginBottom: 24 }}>
        <div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700 }}>{t('admin', 'categories')}</h2>
          <p style={{ color: 'var(--text-muted)', marginTop: 4, fontSize: 14 }}>{categories.length} categories total</p>
        </div>
        <button className="btn btn-primary" onClick={() => { setForm(EMPTY); setModal('new'); }}>
          <Plus size={17} /> {t('admin', 'add_category')}
        </button>
      </div>

      <div className="admin-categories-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px,1fr))', gap: 16 }}>
        {categories.map(cat => (
          <div key={cat.id} className="card" style={{ position: 'relative', overflow: 'hidden', transition: 'var(--transition)' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = cat.color; e.currentTarget.style.boxShadow = `0 8px 30px ${cat.color}20`; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}>
            {/* Color accent bar */}
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: `linear-gradient(90deg, ${cat.color}, ${cat.color}80)` }} />
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginTop: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ width: 52, height: 52, background: `${cat.color}18`, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26 }}>{cat.icon}</div>
                <div>
                  <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 700 }}>{cat.name}</h3>
                  <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 2 }}>{cat.productCount} products</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="btn btn-secondary btn-sm btn-icon" onClick={() => { setForm({ name: cat.name, icon: cat.icon, description: cat.description, color: cat.color }); setModal(cat); }}><Edit size={14} /></button>
                <button className="btn btn-sm btn-icon" style={{ background: 'rgba(244,63,94,0.1)', color: 'var(--danger)', border: 'none' }} onClick={() => handleDelete(cat.id, cat.name)}><Trash2 size={14} /></button>
              </div>
            </div>
            {cat.description && <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 14, lineHeight: 1.5 }}>{cat.description}</p>}
          </div>
        ))}
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(null)}>
          <div className="modal-box" style={{ maxWidth: 520 }}>
            <div className="modal-header">
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700 }}>
                {modal === 'new' ? t('admin', 'add_category') : 'Edit Category'}
              </h2>
              <button onClick={() => setModal(null)} className="btn btn-ghost btn-icon"><X size={20} /></button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSave}>
                <div className="form-group">
                  <label className="form-label">Category Name *</label>
                  <input className="form-input" value={form.name} onChange={e => f('name', e.target.value)} required />
                </div>
                <div className="grid-2">
                  <div className="form-group">
                    <label className="form-label">Icon (emoji)</label>
                    <input className="form-input" value={form.icon} onChange={e => f('icon', e.target.value)} style={{ fontSize: 22 }} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Color</label>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input type="color" value={form.color} onChange={e => f('color', e.target.value)} style={{ width: 44, height: 44, borderRadius: 10, border: 'none', cursor: 'pointer', background: 'none' }} />
                      <input className="form-input" value={form.color} onChange={e => f('color', e.target.value)} style={{ flex: 1 }} />
                    </div>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea className="form-input" rows={3} value={form.description} onChange={e => f('description', e.target.value)} />
                </div>
                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                  <button type="button" className="btn btn-secondary" onClick={() => setModal(null)}>{t('common','cancel')}</button>
                  <button type="submit" className="btn btn-primary"><Save size={16} /> {t('admin','save')}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
